import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User } from '@/api/entities';
import { format, parseISO } from 'date-fns';
import { 
  Activity, 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  Trash2, 
  RefreshCw,
  Monitor,
  Server,
  Database,
  ChevronDown,
  ChevronUp,
  Filter,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export default function ErrorLog() {
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [expandedError, setExpandedError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchErrors = async (pageNum = 1) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) return;

      const response = await fetch('/api/admin/error-logs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: pageNum,
          limit: 50,
          showResolved,
          source: sourceFilter === 'all' ? null : sourceFilter
        })
      });

      if (response.ok) {
        const data = await response.json();
        setErrors(data.logs || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to fetch error logs:', error);
    }
  };

  const loadData = async () => {
    try {
      const user = await User.me();
      setCurrentUser(user);

      const roles = user?.app_metadata?.roles || [];
      const isAdmin = roles.includes('admin') || user?.role === 'admin';
      
      if (!user || !isAdmin) {
        setIsLoading(false);
        return;
      }

      await fetchErrors(1);
    } catch (error) {
      console.error('Error loading error logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchErrors(1);
    }
  }, [showResolved, sourceFilter]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchErrors(page);
    setIsRefreshing(false);
  };

  const handleResolve = async (errorId, resolved) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) return;

      const response = await fetch(`/api/admin/error-logs/${errorId}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolved })
      });

      if (response.ok) {
        await fetchErrors(page);
      }
    } catch (error) {
      console.error('Failed to resolve error:', error);
    }
  };

  const handleDelete = async (errorId) => {
    if (!confirm('Are you sure you want to delete this error log?')) return;

    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) return;

      const response = await fetch(`/api/admin/error-logs/${errorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        await fetchErrors(page);
      }
    } catch (error) {
      console.error('Failed to delete error:', error);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-brand-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'frontend':
        return <Monitor className="w-4 h-4 text-brand-400" />;
      case 'backend':
        return <Server className="w-4 h-4 text-green-400" />;
      case 'supabase':
        return <Database className="w-4 h-4 text-brand-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'border-red-500/30 bg-red-500/5';
      case 'warning':
        return 'border-brand-500/30 bg-brand-500/5';
      default:
        return 'border-orange-500/30 bg-orange-500/5';
    }
  };

  if (currentUser) {
    const roles = currentUser.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin') || currentUser.role === 'admin';
    
    if (!isAdmin) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-light text-white mb-4">Access Denied</h2>
            <p className="text-white/60 font-normal">You need admin privileges to view error logs.</p>
          </div>
        </div>
      );
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-8 h-8 animate-spin text-white mx-auto mb-4" />
          <p className="text-white/60 font-normal">Loading error logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-light text-white tracking-tight">Error Log</h1>
              <p className="text-white/60 text-sm font-normal mt-1">
                {total} {total === 1 ? 'incident' : 'incidents'} recorded
              </p>
            </div>
            
            <Button 
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap items-center gap-4 mb-6 p-4 rounded-xl bg-white/5 border border-white/10"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-white/50" />
            <span className="text-white/70 text-sm font-normal">Filters:</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Label htmlFor="source-filter" className="text-white/60 text-sm font-normal">Source:</Label>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="all" className="text-white">All</SelectItem>
                <SelectItem value="frontend" className="text-white">Frontend</SelectItem>
                <SelectItem value="backend" className="text-white">Backend</SelectItem>
                <SelectItem value="supabase" className="text-white">Supabase</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Switch 
              id="show-resolved" 
              checked={showResolved} 
              onCheckedChange={setShowResolved}
              className="data-[state=checked]:bg-green-500"
            />
            <Label htmlFor="show-resolved" className="text-white/60 text-sm font-normal cursor-pointer">
              Show resolved
            </Label>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          {errors.length === 0 ? (
            <div className="text-center py-16">
              <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-4" />
              <p className="text-white/85 font-normal">No errors found</p>
              <p className="text-white/60 text-sm font-normal mt-1">
                {showResolved ? 'All clear!' : 'Try enabling "Show resolved" to see past incidents'}
              </p>
            </div>
          ) : (
            errors.map((error) => (
              <div
                key={error.id}
                className={`border rounded-xl overflow-hidden transition-all ${
                  error.resolved 
                    ? 'border-green-500/20 bg-green-500/5 opacity-60' 
                    : getSeverityColor(error.severity)
                }`}
              >
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedError(expandedError === error.id ? null : error.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {error.resolved ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        getSeverityIcon(error.severity)
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getSourceIcon(error.source)}
                        <span className="text-white/40 text-xs font-normal uppercase">{error.source}</span>
                        {error.component && (
                          <>
                            <span className="text-white/20">•</span>
                            <span className="text-white/40 text-xs font-normal">{error.component}</span>
                          </>
                        )}
                        {error.route && (
                          <>
                            <span className="text-white/20">•</span>
                            <span className="text-white/40 text-xs font-normal truncate">{error.route}</span>
                          </>
                        )}
                      </div>
                      
                      <p className="text-white text-sm font-normal line-clamp-2 mb-2">
                        {error.message}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs text-white/40 font-normal">
                        <span>
                          {error.created_at 
                            ? format(parseISO(error.created_at), 'MMM dd, yyyy h:mm a')
                            : 'Unknown time'
                          }
                        </span>
                        {error.user_email ? (
                          <>
                            <span className="text-white/20">•</span>
                            <span className="text-brand-400 truncate max-w-[200px]" title={error.user_email}>
                              {error.user_email}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-white/20">•</span>
                            <span className="text-white/30 italic">Guest user</span>
                          </>
                        )}
                        {error.http_status && (
                          <>
                            <span className="text-white/20">•</span>
                            <span className={error.http_status >= 500 ? 'text-red-400' : 'text-brand-400'}>
                              HTTP {error.http_status}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {expandedError === error.id ? (
                        <ChevronUp className="w-4 h-4 text-white/40" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white/40" />
                      )}
                    </div>
                  </div>
                </div>

                {expandedError === error.id && (
                  <div className="px-4 pb-4 border-t border-white/10">
                    <div className="pt-4 space-y-4">
                      {/* User Context Section */}
                      <div className="bg-brand-500/10 border border-brand-500/20 rounded-lg p-3">
                        <p className="text-brand-400 text-xs font-normal mb-2">User Context</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-white/50">Email: </span>
                            <span className="text-white/80">{error.user_email || 'Not logged in (Guest)'}</span>
                          </div>
                          <div>
                            <span className="text-white/50">User ID: </span>
                            <span className="text-white/80 font-mono">{error.user_id || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-white/50">Page: </span>
                            <span className="text-white/80">{error.route || 'Unknown'}</span>
                          </div>
                          <div>
                            <span className="text-white/50">Environment: </span>
                            <span className="text-white/80">{error.environment || 'Unknown'}</span>
                          </div>
                        </div>
                      </div>

                      {error.stack_trace && (
                        <div>
                          <p className="text-white/50 text-xs font-normal mb-2">Stack Trace:</p>
                          <pre className="text-xs text-white/70 bg-black/50 p-3 rounded-lg overflow-x-auto max-h-48 overflow-y-auto font-mono">
                            {error.stack_trace}
                          </pre>
                        </div>
                      )}

                      {error.user_agent && (
                        <div>
                          <p className="text-white/50 text-xs font-normal mb-1">User Agent:</p>
                          <p className="text-xs text-white/70 font-normal break-all">{error.user_agent}</p>
                        </div>
                      )}

                      {error.metadata && Object.keys(error.metadata).length > 0 && (
                        <div>
                          <p className="text-white/50 text-xs font-normal mb-2">Additional Info:</p>
                          <pre className="text-xs text-white/70 bg-black/50 p-3 rounded-lg overflow-x-auto font-mono">
                            {JSON.stringify(error.metadata, null, 2)}
                          </pre>
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolve(error.id, !error.resolved);
                          }}
                          variant="outline"
                          size="sm"
                          className={`text-xs ${
                            error.resolved 
                              ? 'bg-brand-500/10 border-brand-500/30 text-brand-400 hover:bg-brand-500/20' 
                              : 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20'
                          }`}
                        >
                          {error.resolved ? (
                            <>
                              <X className="w-3 h-3 mr-1" />
                              Unresolve
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Mark Resolved
                            </>
                          )}
                        </Button>

                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(error.id);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-xs bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </motion.div>

        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center gap-2 mt-8"
          >
            <Button
              onClick={() => fetchErrors(page - 1)}
              disabled={page <= 1}
              variant="outline"
              size="sm"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30"
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-white/60 text-sm font-normal">
              Page {page} of {totalPages}
            </span>
            <Button
              onClick={() => fetchErrors(page + 1)}
              disabled={page >= totalPages}
              variant="outline"
              size="sm"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30"
            >
              Next
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
